import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications'
import { Capacitor } from '@capacitor/core'
import { supabase } from '../lib/supabase'

interface PushNotificationContextType {
  isRegistered: boolean
  deviceToken: string | null
  registerForPushNotifications: () => Promise<void>
}

const PushNotificationContext = createContext<PushNotificationContextType | undefined>(undefined)

export const usePushNotifications = () => {
  const context = useContext(PushNotificationContext)
  if (!context) {
    throw new Error('usePushNotifications must be used within a PushNotificationProvider')
  }
  return context
}

interface PushNotificationProviderProps {
  children: ReactNode
}

export const PushNotificationProvider = ({ children }: PushNotificationProviderProps) => {
  const [isRegistered, setIsRegistered] = useState(false)
  const [deviceToken, setDeviceToken] = useState<string | null>(null)

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      console.log('Push notifications are only available on native platforms')
      return
    }

    initializePushNotifications()
  }, [])

  const initializePushNotifications = async () => {
    try {
      // 通知権限をチェック
      let permStatus = await PushNotifications.checkPermissions()

      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions()
      }

      if (permStatus.receive !== 'granted') {
        console.log('User denied permissions!')
        return
      }

      // プッシュ通知の登録
      await PushNotifications.register()

      // リスナーの設定
      setupListeners()
    } catch (error) {
      console.error('Error initializing push notifications:', error)
    }
  }

  const setupListeners = () => {
    // 登録成功時
    PushNotifications.addListener('registration', async (token: Token) => {
      console.log('Push registration success, token: ' + token.value)
      setDeviceToken(token.value)
      setIsRegistered(true)

      // デバイストークンをSupabaseに保存
      await saveDeviceToken(token.value)
    })

    // 登録失敗時
    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('Error on registration: ' + JSON.stringify(error))
    })

    // 通知受信時（アプリがフォアグラウンド）
    PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        console.log('Push notification received: ' + JSON.stringify(notification))
        // アプリ内で通知を表示するカスタムUIを実装可能
      }
    )

    // 通知タップ時
    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (notification: ActionPerformed) => {
        console.log('Push notification action performed', notification.actionId, notification.notification)
        // 通知からアプリを開いた場合の処理
        // 例: 特定のタスクページに遷移
      }
    )
  }

  const saveDeviceToken = async (token: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()

      if (!user) {
        console.error('No user logged in')
        return
      }

      const platform = Capacitor.getPlatform() // 'ios' or 'android'

      // デバイストークンをデータベースに保存
      const { error } = await supabase
        .from('device_tokens')
        .upsert({
          user_id: user.id,
          token: token,
          platform: platform,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,token'
        })

      if (error) {
        console.error('Error saving device token:', error)
      } else {
        console.log('Device token saved successfully')
      }
    } catch (error) {
      console.error('Error in saveDeviceToken:', error)
    }
  }

  const registerForPushNotifications = async () => {
    await initializePushNotifications()
  }

  return (
    <PushNotificationContext.Provider value={{ isRegistered, deviceToken, registerForPushNotifications }}>
      {children}
    </PushNotificationContext.Provider>
  )
}
